import React from 'react';

import { ExternalLink } from 'lucide-react';
import { useAccount } from 'wagmi';

import { Button } from '@/components/shadcn/ui/button';
import { CONTRACT_PARAMS, Currency } from '@/constants';
import useExplorerByCurrency from '@/hooks/useExplorerByCurrency';
import { useBtcWallet } from '@/networks/btc-like/btc';
import { useDogeWallet } from '@/networks/btc-like/doge';
import { useLtcWallet } from '@/networks/btc-like/ltc';
import { useSolanaWallet } from '@/networks/svm';
import { useTronWallet } from '@/networks/tron';
import {
  isBtcLikeCurrency,
  isErc20Currency,
  isEvmNativeCurrency,
  isSolanaCurrency,
  isSplCurrency,
  isTronCurrency,
} from '@/utils';

const useBuildTokenWalletLink = (currency: Currency) => {
  const evmAccount = useAccount();
  const btcAccount = useBtcWallet();
  const ltcAccount = useLtcWallet();
  const dogeAccount = useDogeWallet();
  const tronAccount = useTronWallet();
  const solanaAccount = useSolanaWallet();
  const explorerLink = useExplorerByCurrency(currency);

  const ltcLikeAccounts = {
    [Currency.BTC]: btcAccount,
    [Currency.LTC]: ltcAccount,
    [Currency.DOGE]: dogeAccount,
  };

  switch (true) {
    case isBtcLikeCurrency(currency):
      return `${explorerLink}/address/${ltcLikeAccounts[currency].address}`;
    case isEvmNativeCurrency(currency):
      return `${explorerLink}/address/${evmAccount.address}`;
    case isErc20Currency(currency):
      return `${explorerLink}/token/${CONTRACT_PARAMS[currency].L1_TOKEN_ADDRESS}?a=${evmAccount.address}`;
    case isTronCurrency(currency):
      return `${explorerLink}/#/address/${tronAccount.address}`;
    case isSolanaCurrency(currency):
      return `${explorerLink}/account/${solanaAccount.address}`;
    case isSplCurrency(currency):
      return `${explorerLink}/account/${solanaAccount.address}`;
  }
};

interface HeaderWalletDrawerBalanceCardTokenExplorerLinkProps {
  currency: Currency;
}
const HeaderWalletDrawerBalanceCardTokenExplorerLink: React.FC<HeaderWalletDrawerBalanceCardTokenExplorerLinkProps> = ({
  currency,
}) => {
  const tokenExplorerWalletLink = useBuildTokenWalletLink(currency);

  return (
    <Button
      asChild
      variant="secondary"
      className="bg-foreground h-4 rounded-xl p-0 px-1 leading-0"
      onClick={(e) => e.stopPropagation()}
    >
      <a target="_blank" rel="noopener noreferrer" href={tokenExplorerWalletLink}>
        <ExternalLink className="size-2.5" />
      </a>
    </Button>
  );
};

export default HeaderWalletDrawerBalanceCardTokenExplorerLink;
